import { Printer } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { FadeUp } from "../components/Reveal";
import { useContent } from "../context/ContentContext";

const fill = (text, site) =>
  text
    .replaceAll("{naam}", site.name)
    .replaceAll("{email}", site.email)
    .replaceAll("{telefoon}", site.phoneDisplay)
    .replaceAll("{plaats}", site.location);

export default function AlgemeneVoorwaarden() {
  const { site, voorwaarden } = useContent();
  const avArtikelen = voorwaarden.artikelen;
  const avVersie = voorwaarden.versie;
  const bedrijfsregel = [site.address, site.kvk && `KvK ${site.kvk}`, site.btw && `Btw ${site.btw}`].filter(Boolean);

  return (
    <main data-testid="voorwaarden-page">
      <PageHero
        eyebrow="Algemene voorwaarden"
        title="Duidelijke afspraken, geen kleine lettertjes."
        intro={`Deze algemene voorwaarden gelden voor alle offertes, overeenkomsten en werkzaamheden van ${site.name}. Ik heb ze in gewone taal geschreven, in lijn met het Nederlandse consumentenrecht en het bouwrecht (Boek 6 en 7 Burgerlijk Wetboek).`}
      />

      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-4 pb-16 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3 border-y border-[#E2E0D9] py-3 text-xs text-[#8E8D8A]">
            <span data-testid="voorwaarden-versie">{avVersie} · {site.name}, {site.location}</span>
            <button
              type="button"
              data-testid="voorwaarden-print"
              onClick={() => window.print()}
              className="flex items-center gap-1.5 font-display text-xs font-bold uppercase tracking-wide text-[#111111] hover:underline"
            >
              <Printer className="h-3.5 w-3.5" /> Printen of opslaan als PDF
            </button>
          </div>

          <div className="mt-10 max-w-4xl space-y-12">
            {avArtikelen.map((art, i) => (
              <FadeUp key={art.titel}>
                <article data-testid={`voorwaarden-artikel-${i + 1}`} className="grid gap-4 sm:grid-cols-[64px_1fr]">
                  <span className="font-display text-3xl font-bold text-[#B9B7B0]">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h2 className="font-display text-xl font-bold uppercase tracking-wide text-[#111111] sm:text-2xl">{art.titel}</h2>
                    <ol className="mt-4 space-y-3">
                      {art.items.map((item, j) => (
                        <li key={j} className="flex gap-3 text-[15px] leading-relaxed text-[#444444]">
                          <span className="shrink-0 font-display text-sm font-bold text-[#8E8D8A]">{i + 1}.{j + 1}</span>
                          <span>{fill(item, site)}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </article>
              </FadeUp>
            ))}
          </div>

          <FadeUp>
            <div className="mt-16 max-w-4xl border border-[#DDD9D0] bg-[#EFEDE8] p-6 sm:p-8">
              <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-[#8E8D8A]">Goed om te weten</p>
              <h2 className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-[#111111] sm:text-2xl">
                Je wettelijke rechten als consument blijven altijd gelden.
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-[#555555]">
                Niets in deze voorwaarden beperkt de rechten die je als consument hebt op grond van dwingend Nederlands recht.
                Is een bepaling in strijd met de wet, dan geldt in plaats daarvan de wettelijke regeling en blijven de overige
                bepalingen van kracht. Je ontvangt deze voorwaarden bij iedere offerte.
              </p>
              <div className="mt-6 border-t border-[#DDD9D0] pt-4 text-sm text-[#555555]">
                <p className="font-display font-bold uppercase tracking-wide text-[#111111]">{site.name}</p>
                <p className="mt-1">
                  {site.location} · Tel. {site.phoneDisplay} · <a href={`mailto:${site.email}`} className="underline underline-offset-2">{site.email}</a>
                </p>
                {bedrijfsregel.length > 0 && <p data-testid="voorwaarden-bedrijfsgegevens" className="mt-1">{bedrijfsregel.join(" · ")}</p>}
                <p className="mt-2 text-xs text-[#8E8D8A]">{avVersie}. Deze voorwaarden vervangen alle eerdere versies.</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
